import { ComponentPropsWithoutRef, Ref } from 'react';
import * as Headless from '@headlessui/react';
import clsx from 'clsx';

import { TouchTarget } from './button-tw';
import { Link } from './link';
import { TActionItem } from './type';

const colors = {
  red: 'bg-red-500/15 text-red-700 group-data-hover:bg-red-500/25 dark:bg-red-500/10 dark:text-red-400 dark:group-data-hover:bg-red-500/20',
  orange:
    'bg-orange-500/15 text-orange-700 group-data-hover:bg-orange-500/25 dark:bg-orange-500/10 dark:text-orange-400 dark:group-data-hover:bg-orange-500/20',
  amber:
    'bg-amber-400/20 text-amber-700 group-data-hover:bg-amber-400/30 dark:bg-amber-400/10 dark:text-amber-400 dark:group-data-hover:bg-amber-400/15',
  yellow:
    'bg-yellow-400/20 text-yellow-700 group-data-hover:bg-yellow-400/30 dark:bg-yellow-400/10 dark:text-yellow-300 dark:group-data-hover:bg-yellow-400/15',
  lime: 'bg-lime-400/20 text-lime-700 group-data-hover:bg-lime-400/30 dark:bg-lime-400/10 dark:text-lime-300 dark:group-data-hover:bg-lime-400/15',
  green:
    'bg-green-500/15 text-green-700 group-data-hover:bg-green-500/25 dark:bg-green-500/10 dark:text-green-400 dark:group-data-hover:bg-green-500/20',
  emerald:
    'bg-emerald-500/15 text-emerald-700 group-data-hover:bg-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-400 dark:group-data-hover:bg-emerald-500/20',
  teal: 'bg-teal-500/15 text-teal-700 group-data-hover:bg-teal-500/25 dark:bg-teal-500/10 dark:text-teal-300 dark:group-data-hover:bg-teal-500/20',
  cyan: 'bg-cyan-400/20 text-cyan-700 group-data-hover:bg-cyan-400/30 dark:bg-cyan-400/10 dark:text-cyan-300 dark:group-data-hover:bg-cyan-400/15',
  sky: 'bg-sky-500/15 text-sky-700 group-data-hover:bg-sky-500/25 dark:bg-sky-500/10 dark:text-sky-300 dark:group-data-hover:bg-sky-500/20',
  primary:
    'bg-primary-500/15 text-primary-700 group-data-hover:bg-primary-500/25 dark:text-primary-400 dark:group-data-hover:bg-primary-500/25',
  indigo:
    'bg-indigo-500/15 text-indigo-700 group-data-hover:bg-indigo-500/25 dark:text-indigo-400 dark:group-data-hover:bg-indigo-500/20',
  violet:
    'bg-primary-500/15 text-primary-700 group-data-hover:bg-primary-500/25 dark:text-primary-400 dark:group-data-hover:bg-primary-500/20',
  purple:
    'bg-primary-500/15 text-primary-700 group-data-hover:bg-primary-500/25 dark:text-primary-400 dark:group-data-hover:bg-primary-500/20',
  fuchsia:
    'bg-fuchsia-400/15 text-fuchsia-700 group-data-hover:bg-fuchsia-400/25 dark:bg-fuchsia-400/10 dark:text-fuchsia-400 dark:group-data-hover:bg-fuchsia-400/20',
  pink: 'bg-pink-400/15 text-pink-700 group-data-hover:bg-pink-400/25 dark:bg-pink-400/10 dark:text-pink-400 dark:group-data-hover:bg-pink-400/20',
  rose: 'bg-rose-400/15 text-rose-700 group-data-hover:bg-rose-400/25 dark:bg-rose-400/10 dark:text-rose-400 dark:group-data-hover:bg-rose-400/20',
  zinc: 'bg-gray-600/10 text-gray-700 group-data-hover:bg-gray-600/20 dark:bg-white/5 dark:text-gray-400 dark:group-data-hover:bg-white/10',
};

/**
 * Configuration properties for the Badge component.
 */
type BadgeProps = { 
  /** The color variant of the badge. */
  color?: keyof typeof colors 
};

/**
 * -----------------------------------------------------------------------------
 * This renders a colorful non interactive badge component
 */
export function Badge({
  color = 'primary',
  className,
  ...props
}: BadgeProps & ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'inline-flex items-center gap-x-1.5 rounded-md px-1.5 py-0.5 text-sm/5',
        'forced-colors:outline font-medium sm:text-xs/5',
        colors[color]
      )}
    />
  );
}

/**
 * Configuration properties for the BadgeButton component.
 */
type TBadgeButtonProps = BadgeProps & TActionItem;

/**
 * -----------------------------------------------------------------------------
 * This renders a colorful interactive badge component
 */
export function BadgeButton({
  color = 'zinc',
  className,
  children,
  ref,
  ...props
}: TBadgeButtonProps) {
  const classes = clsx(
    className,
    'group relative inline-flex rounded-md focus:outline-hidden',
    'data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary-500'
  );

  return 'href' in props ? (
    <Link {...props} ref={ref as Ref<HTMLAnchorElement>} className={classes}>
      <TouchTarget>
        <Badge color={color}>{children}</Badge>
      </TouchTarget>
    </Link>
  ) : (
    <Headless.Button {...props} ref={ref} className={classes}>
      <TouchTarget>
        <Badge color={color}>{children}</Badge>
      </TouchTarget>
    </Headless.Button>
  );
}
